import React, { useEffect, useState } from "react";
import { FileText } from "lucide-react";
import {
  dashboardService,
  WorkflowTemplateStatusData,
} from "../services/dashboard.service";

import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const COLORS = {
  total: "#3b82f6",
  active: "#059669",
  inactive: "#6b7280",
};

const WorkflowTemplateStatus: React.FC = () => {
  const [data, setData] = useState<WorkflowTemplateStatusData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await dashboardService.getWorkflowTemplateStatus();
      if (result.success && result.data) {
        setData(result.data);
      }
    } catch (err) {
      console.error("Error fetching workflow template status:", err);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !data) return null;

  /** DATA FOR FULL-WIDTH HORIZONTAL BAR CHART */
  const chartData = [
    {
      label: "Total",
      value: data.total_workflow_template,
      color: COLORS.total,
    },
    {
      label: "Active",
      value: data.total_active_workflow_template,
      color: COLORS.active,
    },
    {
      label: "Inactive",
      value: data.total_inactive_workflow_template,
      color: COLORS.inactive,
    },
  ];

  return (
    <div className="bg-white rounded-md border border-gray-200  overflow-hidden">
      {/* HEADER */}
      <div className="px-6 py-4 border-b border-gray-100 bg-white/80 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-lg bg-gray-50 border border-gray-200">
            <FileText className="w-5 h-5 text-gray-700" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Workflow Templates
            </h3>
            <p className="text-sm text-gray-500">
              Full-width horizontal comparison
            </p>
          </div>
        </div>
      </div>

      {/* CHART SECTION */}
      <div className="p-6">
        <div className="h-44 w-full">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" barCategoryGap={18}>
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="label"
                tick={{ fontSize: 12, fill: "#4b5563" }}
              />
              <Tooltip />

              <Bar dataKey="value" fill="#8884d8" barSize={18}>
                {chartData.map((item, index) => (
                  <Cell key={`cell-${index}`} fill={item.color} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* COUNTS BELOW */}
        <div className="grid grid-cols-3 gap-4 mt-6 text-center">
          <div>
            <p className="text-xl font-bold text-blue-600">
              {data.total_workflow_template}
            </p>
            <p className="text-xs text-gray-500">Total</p>
          </div>

          <div>
            <p className="text-xl font-bold text-emerald-600">
              {data.total_active_workflow_template}
            </p>
            <p className="text-xs text-gray-500">Active</p>
          </div>

          <div>
            <p className="text-xl font-bold text-gray-600">
              {data.total_inactive_workflow_template}
            </p>
            <p className="text-xs text-gray-500">Inactive</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowTemplateStatus;
